import React, { useState, useEffect, useContext } from 'react';
import {
    Container,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    CloudUpload as UploadIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { userContext } from '../../stores/users/userContext';
import { messageContext } from '../../stores/messages/messageContext';
import { hostApi } from '../constants';
import { ROUTES } from '../../constants/routes';
import apiRequest from '../../utils/apiClient';

const SubscriptionSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const userStore = useContext(userContext);
    const messageStore = useContext(messageContext);
    const [loading, setLoading] = useState(true);
    const [subscriptionData, setSubscriptionData] = useState(null);

    const rawSessionId = searchParams.get('session_id');
    // Clean session ID by removing any wrapping braces
    const sessionId = rawSessionId ? rawSessionId.replace(/^{|}$/g, '') : null;

    useEffect(() => {
        // Debug: Log URL and parameters
        console.log('=== SUBSCRIPTION SUCCESS DEBUG ===');
        console.log('Current URL:', window.location.href);
        console.log('Search params toString():', searchParams.toString());
        console.log('Raw session ID from params:', rawSessionId);
        console.log('Cleaned session ID:', sessionId);
        console.log('All URL search params:', Array.from(searchParams.entries()));
        console.log('Window location search:', window.location.search);
        console.log('Window location pathname:', window.location.pathname);
        console.log('===================================');

        const verifyPayment = async () => {
            if (!sessionId) {
                console.error('❌ No session ID found!');
                console.log('All params available:', Array.from(searchParams.entries()));
                setLoading(false);
                return;
            }

            // No more demo mode - using real backend API

            try {
                const formData = new FormData();
                formData.append('session_id', sessionId);

                console.log('Sending session_id to backend:', sessionId);
                console.log('Session ID length:', sessionId.length);

                const response = await apiRequest(`${hostApi}verify-payment/`, {
                    method: 'POST',
                    body: formData,
                });

                const data = await response.json();

                if (response.ok) {
                    console.log('=== SUBSCRIPTION SUCCESS DATA ===');
                    console.log('Response data:', data);
                    console.log('mode:', data.mode);
                    console.log('plan_name:', data.plan_name);
                    console.log('status:', data.status);
                    console.log('===============================');

                    setSubscriptionData(data);
                    
                    // Handle different messages based on mode
                    if (data.mode === 'setup' && data.payment_method_updated) {
                        messageStore.setMessage('Payment method updated successfully!', 'success');
                    } else {
                        messageStore.setMessage('Subscription activated successfully!', 'success');
                    }
                    
                    // Refresh user info to get updated subscription status
                    if (userStore.isUserAuth()) {
                        await userStore.getUserInfo();
                    }
                } else {
                    messageStore.setMessage(data.error || 'Failed to verify payment', 'error');
                }
            } catch (err) {
                console.error('Payment verification error:', err);
                messageStore.setMessage('Failed to verify payment. Please contact support.', 'error');
            } finally {
                setLoading(false);
            }
        };

        verifyPayment();
    }, [sessionId, userStore, messageStore]);

    // These used to point at /upload-video/ and /transcripts/, which are not routes — a
    // customer who had just paid landed on the 404 page. Use the real ones.
    const handleStartUpload = () => {
        navigate(ROUTES.HOME);
    };

    const handleGetPlugin = () => {
        navigate(ROUTES.PLUGIN_DOWNLOAD);
    };

    const handleViewPlans = () => {
        navigate(ROUTES.SUBSCRIPTION);
    };

    const handleBackToManagement = () => {
        navigate(ROUTES.SUBSCRIPTION_MANAGE);
    };

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Verifying your payment...
                </Typography>
            </Container>
        );
    }

    if (!subscriptionData) {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Box textAlign="center">
                    <Typography variant="h4" gutterBottom>
                        Payment Verification Failed
                    </Typography>
                    <Typography variant="body1" paragraph>
                        We couldn't verify your payment. Please check your email for confirmation
                        or contact support if the issue persists.
                    </Typography>
                    <Button variant="contained" onClick={handleViewPlans}>
                        Back to Plans
                    </Button>
                </Box>
            </Container>
        );
    }

    // Check if this is a payment method update (setup mode)
    const isPaymentMethodUpdate = subscriptionData?.mode === 'setup' && subscriptionData?.payment_method_updated;

    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Card sx={{ textAlign: 'center', p: 4 }}>
                <CardContent>
                    <CheckCircleIcon
                        sx={{
                            fontSize: 80,
                            color: 'success.main',
                            mb: 2
                        }}
                    />

                    {isPaymentMethodUpdate ? (
                        <>
                            <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
                                Payment Method Updated
                            </Typography>

                            <Typography variant="h6" color="text.secondary" paragraph>
                                Your payment method has been updated successfully
                            </Typography>

                            <Typography variant="body1" paragraph sx={{ mb: 4, mt: 2 }}>
                                Your new payment method is now set as the default and will be used for all future charges.
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleBackToManagement}
                                    sx={{ py: 1.5 }}
                                >
                                    Back to Subscription Management
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
                                Welcome to {subscriptionData?.plan_name || 'Premium'}!
                            </Typography>

                            <Typography variant="h6" color="text.secondary" paragraph>
                                Your subscription has been activated successfully
                            </Typography>

                            <Box sx={{ my: 4 }}>
                                <Divider sx={{ my: 2 }} />

                                <Typography variant="body1" paragraph>
                                    <strong>Plan:</strong> {subscriptionData?.plan_name || 'Loading...'}
                                </Typography>

                                <Typography variant="body1" paragraph>
                                    <strong>Status:</strong> {subscriptionData?.status || 'Loading...'}
                                </Typography>

                                {subscriptionData?.trial_end && (
                                    <Typography variant="body1" paragraph>
                                        <strong>Trial ends:</strong> {new Date(subscriptionData.trial_end).toLocaleDateString()}
                                    </Typography>
                                )}

                                {subscriptionData?.next_billing && (
                                    <Typography variant="body1" paragraph>
                                        <strong>Next billing:</strong> {new Date(subscriptionData.next_billing).toLocaleDateString()}
                                    </Typography>
                                )}

                                <Divider sx={{ my: 2 }} />
                            </Box>

                            {/* Honest copy: nothing here is unlimited. The real numbers live on
                                the plan (see Account > Usage), so don't restate them and risk
                                drifting out of sync — point at where they're true. */}
                            <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                                Your new transcription hours, storage and export limits are active now.
                                You can see exactly what you've used at any time under Account → Usage.
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<UploadIcon />}
                                    onClick={handleStartUpload}
                                    sx={{ py: 1.5 }}
                                >
                                    Start Uploading a Video
                                </Button>

                                {/* The moment someone pays is the moment to hand them the panel. */}
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleGetPlugin}
                                    sx={{ py: 1.5 }}
                                >
                                    Get the Premiere Pro panel (macOS)
                                </Button>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
                                Need help getting started?{' '}
                                <Button variant="text" size="small" onClick={() => navigate(ROUTES.CONTACT)}>
                                    Contact support
                                </Button>
                            </Typography>
                        </>
                    )}
                </CardContent>
            </Card>
        </Container>
    );
};

export default SubscriptionSuccess; 